import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AUTH_ACCESS_KEY, AUTH_OWNER_IMPERSONATION_KEY, AUTH_REFRESH_KEY, AUTH_USER_KEY } from '@/constants/auth-storage'
import { authApi } from '@/services/api/auth'
import type { StaffUser } from '@/types/staff-user'

const ACCESS_KEY = AUTH_ACCESS_KEY
const REFRESH_KEY = AUTH_REFRESH_KEY
const USER_KEY = AUTH_USER_KEY
const OWNER_IMPERSONATION_KEY = AUTH_OWNER_IMPERSONATION_KEY

export interface OwnerImpersonationState {
  actorEmail?: string | null
  actorId?: string | null
  actorRole?: string | null
  merchantUserId?: number | null
  startedAt: string
}

function pickRefreshToken(data: { refreshToken?: string; longLivedToken?: string }): string {
  return data.refreshToken || data.longLivedToken || ''
}

/** Client-side JWT exp check (no signature verify). Used to refresh before the first API call after reload. */
function parseJwtPayload(accessToken: string): Record<string, unknown> | null {
  try {
    const parts = accessToken.split('.')
    if (parts.length !== 3) return null
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4
    if (pad) b64 += '='.repeat(4 - pad)
    const json = atob(b64)
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

function isAccessTokenExpired(accessToken: string, skewSeconds: number): boolean {
  const p = parseJwtPayload(accessToken)
  const exp = p?.exp
  if (typeof exp !== 'number') return true
  return Date.now() / 1000 >= exp - skewSeconds
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<StaffUser | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  /** Full-screen lock overlay (Paulus CRM–style) after successful login / during logout */
  const isLoggingIn = ref(false)
  const isLoggingOut = ref(false)
  const ownerImpersonation = ref<OwnerImpersonationState | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value && user.value))

  function persist(
    accessToken: string,
    userData: StaffUser,
    refreshPlain: string,
    impersonation: OwnerImpersonationState | null = ownerImpersonation.value,
  ) {
    token.value = accessToken
    user.value = userData
    localStorage.setItem(ACCESS_KEY, accessToken)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
    refreshToken.value = refreshPlain
    localStorage.setItem(REFRESH_KEY, refreshPlain)
    ownerImpersonation.value = impersonation
    if (impersonation) {
      localStorage.setItem(OWNER_IMPERSONATION_KEY, JSON.stringify(impersonation))
    } else {
      localStorage.removeItem(OWNER_IMPERSONATION_KEY)
    }
  }

  /** Merges an updated staff user (e.g. profile PATCH) into session and localStorage. */
  function mergeSessionUser(partial: StaffUser) {
    const current = user.value
    if (!current) return
    const next: StaffUser = {
      ...current,
      ...partial,
      role: partial.role ?? current.role,
    }
    user.value = next
    localStorage.setItem(USER_KEY, JSON.stringify(next))
  }

  function clear() {
    token.value = null
    user.value = null
    refreshToken.value = null
    ownerImpersonation.value = null
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(OWNER_IMPERSONATION_KEY)
  }

  /**
   * Restores session from localStorage and refreshes the access token when it is missing or expired
   * (so the first guarded route / API call does not rely on a silent 401 → refresh chain).
   */
  async function initializeFromStorage() {
    const a = localStorage.getItem(ACCESS_KEY)
    const r = localStorage.getItem(REFRESH_KEY)
    const u = localStorage.getItem(USER_KEY)
    const o = localStorage.getItem(OWNER_IMPERSONATION_KEY)

    if (!u) {
      if (a || r) clear()
      return
    }

    let parsedUser: StaffUser
    try {
      parsedUser = JSON.parse(u) as StaffUser
    } catch {
      clear()
      return
    }

    if (!a && !r) {
      clear()
      return
    }

    user.value = parsedUser
    token.value = a
    refreshToken.value = r
    if (o) {
      try {
        ownerImpersonation.value = JSON.parse(o) as OwnerImpersonationState
      } catch {
        ownerImpersonation.value = null
        localStorage.removeItem(OWNER_IMPERSONATION_KEY)
      }
    } else {
      ownerImpersonation.value = null
    }

    const canRefresh = Boolean(r)
    const accessMissing = !a
    const accessStale = Boolean(a && isAccessTokenExpired(a, 120))

    if (canRefresh && (accessMissing || accessStale)) {
      try {
        await refreshAccessToken()
      } catch {
        clear()
      }
      return
    }

    if (!canRefresh && accessStale) {
      clear()
      return
    }

    if (a) {
      try {
        await refreshSessionUser(a)
      } catch {
        if (canRefresh) {
          try {
            await refreshAccessToken()
          } catch {
            clear()
          }
        }
      }
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true
    try {
      const res = await authApi.loginStaff({ email, password })
      await acceptPasswordResetSession(res)
    } finally {
      isLoading.value = false
      isLoggingIn.value = false
    }
  }

  async function refreshAccessToken() {
    const rt = refreshToken.value || localStorage.getItem(REFRESH_KEY)
    if (!rt) {
      clear()
      throw new Error('No refresh token')
    }
    const data = await authApi.refreshAccessToken(rt)
    const nextRt = pickRefreshToken(data)
    if (!nextRt) {
      clear()
      throw new Error('No refresh token after refresh')
    }
    token.value = data.accessToken
    refreshToken.value = nextRt
    localStorage.setItem(ACCESS_KEY, data.accessToken)
    localStorage.setItem(REFRESH_KEY, nextRt)
    if (data.user) {
      user.value = data.user
      localStorage.setItem(USER_KEY, JSON.stringify(data.user))
    }
  }

  async function refreshSessionUser(accessToken: string) {
    const data = await authApi.authenticateStaffToken(accessToken)
    user.value = data.user
    localStorage.setItem(USER_KEY, JSON.stringify(data.user))
  }

  function acceptOwnerImpersonationSession(session: {
    accessToken: string
    refreshToken: string
    user: StaffUser
    ownerImpersonation?: OwnerImpersonationState | null
  }) {
    persist(
      session.accessToken,
      session.user,
      session.refreshToken,
      session.ownerImpersonation ?? {
        startedAt: new Date().toISOString(),
      },
    )
  }

  async function acceptPasswordResetSession(session: {
    accessToken?: string
    authentication?: { accessToken?: string }
    refreshToken?: string
    longLivedToken?: string
    user: StaffUser
  }) {
    isLoggingIn.value = true
    try {
      const access = session.accessToken || session.authentication?.accessToken
      if (!access) throw new Error('No access token in response')
      const rt = pickRefreshToken(session)
      if (!rt) throw new Error('No refresh token in response')
      persist(access, session.user, rt, null)
      await new Promise((r) => setTimeout(r, 1_400))
    } finally {
      isLoggingIn.value = false
    }
  }

  async function logout() {
    isLoggingOut.value = true
    const rt = refreshToken.value
    try {
      if (token.value) {
        await authApi.logoutStaff(rt ?? undefined).catch(() => {})
      }
      await new Promise((r) => setTimeout(r, 1_400))
    } catch {
      // Always clear local session
    } finally {
      clear()
      isLoggingOut.value = false
    }
  }

  return {
    user,
    token,
    refreshToken,
    isLoading,
    isLoggingIn,
    isLoggingOut,
    ownerImpersonation,
    isAuthenticated,
    initializeFromStorage,
    login,
    logout,
    refreshAccessToken,
    mergeSessionUser,
    acceptOwnerImpersonationSession,
    acceptPasswordResetSession,
    clear,
  }
})
